import { FormEvent, PropsWithChildren, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Else, If, Then, When } from 'react-if';
import { usePreservedCallback } from '@toss/react';
import cns from 'classnames';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { ChevronLeftIcon, CreditCardIcon, RotateCircleIcon } from '@zep/icons';
import { Button, Input, Spacing, TextField } from '@zep/ui';
import useCopyPrevention from '../../../hooks/useCopyPrevention';
import { PaymentForm, RegisterPaymentSchemaType } from '../../../paymentForm';
import { PlanCheckoutFormSchemaType, useSubscriptionPaymentContext } from '../../hooks';
import S from './PlanCheckoutPayment.module.scss';
export const PlanCheckoutPayment = () => {
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['subscription']);
  const {
    isUpdated,
    isUpdatePayment,
    handleIsUpdatedPayment,
    handleStep
  } = useSubscriptionPaymentContext();
  const {
    formState,
    reset
  } = useFormContext<RegisterPaymentSchemaType>();
  const showResetDialog = () => {
    return dialog.confirm({
      title: t('subscription:payment.alertCardCancel.title'),
      content: t('subscription:payment.alertCardCancel.description'),
      confirmText: t('subscription:payment.alertCardCancel.buttonConfirm'),
      cancelText: t('subscription:payment.alertCardCancel.buttonCancel')
    });
  };
  const handleHistoryState = async () => {
    const action = () => {
      isUpdatePayment ? handleIsUpdatedPayment(false) : handleStep(1);
    };
    if (!formState.isDirty) {
      action();
      return;
    }
    const confirm = await showResetDialog();
    if (confirm) {
      reset();
      action();
    }
  };
  const handleReload = usePreservedCallback(e => {
    if (formState.isDirty) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
  useEffect(() => {
    window.addEventListener('beforeunload', handleReload);
    return () => {
      window.removeEventListener('beforeunload', handleReload);
    };
  }, [formState.isDirty, handleReload]);
  return <SubscriptionRegisterSection onHeaderButtonClick={handleHistoryState} data-sentry-element="SubscriptionRegisterSection" data-sentry-component="PlanCheckoutPayment" data-sentry-source-file="PlanCheckoutPayment.tsx">
      <If condition={isUpdated && !isUpdatePayment} data-sentry-element="If" data-sentry-source-file="PlanCheckoutPayment.tsx">
        <Then data-sentry-element="Then" data-sentry-source-file="PlanCheckoutPayment.tsx">
          <PaymentUserInfo onChangePaymentCard={() => handleIsUpdatedPayment(true)} data-sentry-element="PaymentUserInfo" data-sentry-source-file="PlanCheckoutPayment.tsx" />
        </Then>
        <Else data-sentry-element="Else" data-sentry-source-file="PlanCheckoutPayment.tsx">
          <Form data-sentry-element="Form" data-sentry-source-file="PlanCheckoutPayment.tsx" />
        </Else>
      </If>
    </SubscriptionRegisterSection>;
};
const Form = () => {
  const {
    watch
  } = useFormContext<RegisterPaymentSchemaType>();
  useCopyPrevention();
  const isBusiness = watch('isBusiness');
  return <div className="flex max-w-[400px] flex-col gap-[1.625rem]" data-sentry-component="Form" data-sentry-source-file="PlanCheckoutPayment.tsx">
      <PaymentForm.InputCardNumber size={'sm'} data-sentry-element="unknown" data-sentry-source-file="PlanCheckoutPayment.tsx" />
      <div className={S.row}>
        <PaymentForm.InputExpireDate size={'sm'} data-sentry-element="unknown" data-sentry-source-file="PlanCheckoutPayment.tsx" />
      </div>
      {isBusiness ? <PaymentForm.InputIncNumber size={'sm'} /> : <PaymentForm.InputBirthDate size={'sm'} />}
      <PaymentForm.InputCardName size={'sm'} data-sentry-element="unknown" data-sentry-source-file="PlanCheckoutPayment.tsx" />
      <PaymentForm.IncCheckBox data-sentry-element="unknown" data-sentry-source-file="PlanCheckoutPayment.tsx" />
    </div>;
};
export const PaymentUserInfo = ({
  onChangePaymentCard: _onChangePaymentCard
}: {
  onChangePaymentCard: () => void;
}) => {
  const {
    billingInfo,
    space
  } = useSubscriptionPaymentContext();
  const {
    t
  } = useTranslation(['subscription']);
  const {
    formState: {
      errors
    },
    register,
    setValue
  } = useFormContext<PlanCheckoutFormSchemaType>();
  const {
    ref,
    onChange,
    name
  } = register('zipCode');
  const isShowZipCodeInput = !(space?.countryCode === 'KR' || space?.countryCode === 'JP');
  const validateZipCodeInput = (event: FormEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
    setValue('zipCode', value);
  };
  useCopyPrevention();
  const onChangePaymentCard = () => {
    logger.track('payment_subscription_card_change');
    _onChangePaymentCard();
  };
  return <div className={S.billing_info} data-sentry-component="PaymentUserInfo" data-sentry-source-file="PlanCheckoutPayment.tsx">
      <div className={S.billing_info_title}>
        <span>{t('subscription:payment.cardChange.title')}</span>
        <span>{t('subscription:payment.cardChange.description')}</span>
      </div>
      <div className={S.billing_info_card}>
        <CreditCardIcon width={22} height={22} data-sentry-element="CreditCardIcon" data-sentry-source-file="PlanCheckoutPayment.tsx" />
        {billingInfo?.cardName}
      </div>
      <Button className="w-[120px]" variant={'sub'} size={'xs'} onClick={onChangePaymentCard} leftSlot={<RotateCircleIcon width={14} height={14} />} data-sentry-element="Button" data-sentry-source-file="PlanCheckoutPayment.tsx">
        <span>
          {t('subscription:SpaceDetail.menuManage.billingCardChangeButton')}
        </span>
      </Button>
      <When condition={isShowZipCodeInput} data-sentry-element="When" data-sentry-source-file="PlanCheckoutPayment.tsx">
        <Spacing size={32} data-sentry-element="Spacing" data-sentry-source-file="PlanCheckoutPayment.tsx" />
        <div className={S.billing_info_title} style={{
        maxWidth: '400px'
      }}>
          <TextField label={t('subscription:payment.zipCode.title')} error={!!errors?.zipCode?.message} helperText={t(errors?.zipCode?.message as 'subscription:payment.zipCode.minErrorText')} data-sentry-element="TextField" data-sentry-source-file="PlanCheckoutPayment.tsx">
            <Input name={name} ref={ref} onChange={onChange} size={'sm'} type="number" maxLength={10} onInput={validateZipCodeInput} placeholder={t('subscription:payment.zipCode.placeholder')} isFullWidth error={!!errors?.zipCode?.message} data-sentry-element="Input" data-sentry-source-file="PlanCheckoutPayment.tsx" />
          </TextField>
        </div>
      </When>
    </div>;
};
export const SubscriptionRegisterSection = (props: PropsWithChildren<{
  onHeaderButtonClick: () => void;
}>) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    isUpdatePayment
  } = useSubscriptionPaymentContext();
  return <div className={cns(S.right, S.field_group)} data-sentry-component="SubscriptionRegisterSection" data-sentry-source-file="PlanCheckoutPayment.tsx">
      <Button size={'xs'} leftSlot={<ChevronLeftIcon width={14} height={14} />} variant={'sub'} onClick={props.onHeaderButtonClick} data-sentry-element="Button" data-sentry-source-file="PlanCheckoutPayment.tsx">
        {isUpdatePayment ? t('subscription:payment.button.cardChangeCancel') : t('subscription:payment.button.backStep')}
      </Button>
      {props.children}
    </div>;
};